import { useAppMentionOptions } from "@/hooks/use-app-mention-options";
import {
  AppModal,
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormSubmitButton,
  SwitchFormField,
  Typography,
} from "@/components/ui";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { useUser } from "@/components/UserProvider";
import { useTiptapMentions } from "@/hooks/useTiptapMentions";
import {
  getUsersOptions,
  retrieveMatterNotesQueryOptions,
  useCreateMatterNote,
  useDeleteMatterNote,
  useUpdateMatterNote,
} from "@/lib/data/react-query";
import { retrieveMatterQueryOptions } from "@/lib/data/react-query/matters/matter";
import { MatterNoteSchema } from "@/lib/data/schemas";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { type MatterNote, type MatterSubItemType } from "@/lib/types";
import { mergeInitialData } from "@/lib/utils/initialDataUtils";
import {} from "@/lib/utils/mentionUtils";
import { slideoverFieldsetClassName } from "@/lib/utils/slideover-classes";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { EditorContent } from "@tiptap/react";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

interface AddOrEditMatterNoteModalProps {
  title: string;
  description?: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  editing?: boolean;
  initialTab?: "details" | "notes" | "tasks" | "references" | "events";
  matterId: string;
  noteId?: string;
  initialData?: Partial<MatterNote>;
  subItem?: {
    id: string;
    type: MatterSubItemType;
  };
}

const formSchema = z.object({
  message: MatterNoteSchema.shape.message.min(1, {
    message: "Message is required",
  }),
  draft: MatterNoteSchema.shape.draft,
  pinned: MatterNoteSchema.shape.pinned,
});

const defaultObject = {
  message: "",
  draft: false,
  pinned: false,
};

const noteModalFormId = "add-or-edit-matter-note-modal-form";

export const AddOrEditMatterNoteModal = ({
  open,
  setOpen,
  editing,
  matterId,
  noteId,
  initialData,
  subItem,
  title,
  description,
}: AddOrEditMatterNoteModalProps) => {
  const { currentUser, hasActionPermission } = useUser();

  const hasEditPermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matter,
    ACTION_PERMISSION_VALUES.update,
  );
  const hasDeletePermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matter,
    ACTION_PERMISSION_VALUES.update,
  );

  const {
    data: matterData,
    isLoading: isMatterLoading,
    error: matterError,
  } = useQuery({
    ...retrieveMatterQueryOptions(matterId),
    enabled: open && Boolean(matterId),
  });
  const { data: matterNotesResponse } = useQuery({
    ...retrieveMatterNotesQueryOptions({ matterId, pageSize: 1000 }),
    enabled: open && Boolean(matterId),
  });

  const activeMatterNote =
    noteId && matterNotesResponse
      ? matterNotesResponse.data.find((note) => note.id === noteId)
      : undefined;
  const noteInitialValues = mergeInitialData(activeMatterNote, initialData);

  const matter = matterData?.data;

  const { data: firmUsersResponse, isLoading: isFirmUsersLoading } = useQuery(
    getUsersOptions(open),
  );

  const firmUsers = useMemo(
    () => firmUsersResponse?.data ?? [],
    [firmUsersResponse],
  );

  const mentionOptions = useAppMentionOptions(
    firmUsers,
    open,
    noteInitialValues?.mentionedApps,
  );

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    values:
      editing && noteInitialValues
        ? { ...defaultObject, ...noteInitialValues }
        : defaultObject,
    mode: "onSubmit",
  });
  const createMatterNote = useCreateMatterNote();
  const updateMatterNote = useUpdateMatterNote();
  const deleteMatterNote = useDeleteMatterNote();
  const isFormLoading =
    createMatterNote.isPending ||
    updateMatterNote.isPending ||
    deleteMatterNote.isPending;

  const isNoteOwner = currentUser.id === activeMatterNote?.userId;
  const canEdit =
    Boolean(activeMatterNote) && (hasEditPermission || isNoteOwner);
  const canDelete =
    Boolean(activeMatterNote) && (hasDeletePermission || isNoteOwner);

  const { editor, getBackendMessage, reset, isSettingEditorContentRef } =
    useTiptapMentions({
      mentionOptions,
      isLoading: isFirmUsersLoading,
      onUpdate: (text) => {
        form.setValue("message", text, {
          shouldDirty: !isSettingEditorContentRef.current,
          shouldTouch: !isSettingEditorContentRef.current,
        });
      },
      initialContent: noteInitialValues?.message ?? activeMatterNote?.message,
      isEditable: editing ? !!canEdit : true,
      className: "min-h-[120px]",
    });

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    if (!matter) return;
    if (editing && activeMatterNote && !canEdit) return;

    const messageForBackend = getBackendMessage();

    const payload = {
      ...data,
      message: messageForBackend,
    };

    setOpen(false);
    if (editing && activeMatterNote) {
      updateMatterNote.mutate({
        matterId,
        id: activeMatterNote.id,
        dateModified: activeMatterNote.dateModified,
        fileId: activeMatterNote.fileId,
        ...payload,
      });
    } else {
      await createMatterNote
        .mutateAsync({
          matterId,
          ...payload,
          userId: currentUser.id,
          vehicleId:
            subItem && subItem.type === "vehicle" ? subItem.id : undefined,
          incidentReportId:
            subItem && subItem.type === "incident-report"
              ? subItem.id
              : undefined,
          insuranceId:
            subItem && subItem.type === "insurance" ? subItem.id : undefined,
          medicalTreatmentId:
            subItem && subItem.type === "medical-treatment"
              ? subItem.id
              : undefined,
          medicalTreatmentBillId:
            subItem && subItem.type === "medical-treatment-bill"
              ? subItem.id
              : undefined,
          medicalTreatmentAppointmentId:
            subItem && subItem.type === "medical-treatment-appointment"
              ? subItem.id
              : undefined,
          medicalTreatmentRecordId:
            subItem && subItem.type === "medical-treatment-record"
              ? subItem.id
              : undefined,
          medicalTreatmentPlanId:
            subItem && subItem.type === "medical-treatment-plan"
              ? subItem.id
              : undefined,
          medicalTreatmentBalanceConfirmationId:
            subItem && subItem.type === "medical-treatment-balance-confirmation"
              ? subItem.id
              : undefined,
          settlementDemandId:
            subItem && subItem.type === "settlement-demand"
              ? subItem.id
              : undefined,
          settlementOfferId:
            subItem && subItem.type === "settlement-offer"
              ? subItem.id
              : undefined,
          settlementCounterId:
            subItem && subItem.type === "settlement-counter"
              ? subItem.id
              : undefined,
          expenseId:
            subItem && subItem.type === "expense" ? subItem.id : undefined,
          lostWagesId:
            subItem && subItem.type === "lost-wage" ? subItem.id : undefined,
          lienId: subItem && subItem.type === "lien" ? subItem.id : undefined,
          propertyDamageId:
            subItem && subItem.type === "property-damage"
              ? subItem.id
              : undefined,
          signatureRequestId:
            subItem && subItem.type === "signature-request"
              ? subItem.id
              : undefined,
          trustCheckId:
            subItem && subItem.type === "trust-check" ? subItem.id : undefined,
          litigationDateId:
            subItem && subItem.type === "matter-litigation-date"
              ? subItem.id
              : undefined,
          statuteOfLimitationsId:
            subItem && subItem.type === "statute-of-limitations"
              ? subItem.id
              : undefined,
          emailId: subItem && subItem.type === "email" ? subItem.id : undefined,
        })
        .then(() => {
          form.reset();
          reset();
        });
    }
  };

  const handleDelete = () => {
    if (!activeMatterNote || !canDelete) return;

    deleteMatterNote.mutate({
      matterId,
      id: activeMatterNote.id,
    });
    setOpen(false);
  };

  useEffect(() => {
    if (!open) {
      form.reset();
      reset();
    }
  }, [form, open, reset]);

  const showForm = Boolean(matter) && !matterError;

  return (
    <Form {...form}>
      <AppModal
        open={open}
        onOpenChange={setOpen}
        title={title}
        description={description}
        size="2xl"
        loading={isFormLoading || isMatterLoading}
        isDirty={form.formState.isDirty}
        bodyFormId={showForm ? noteModalFormId : undefined}
        bodyFormOnSubmit={
          showForm
            ? async (e) => {
                e.preventDefault();
                e.stopPropagation();
                await form.handleSubmit(onSubmit)();
              }
            : undefined
        }
        footer={
          showForm ? (
            <div className="flex w-full flex-wrap items-center justify-between gap-2">
              <div className="min-w-0">
                {canDelete && editing && activeMatterNote && (
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={handleDelete}
                  >
                    Delete
                  </Button>
                )}
              </div>
              <div className="flex flex-wrap justify-end gap-2">
                <FormCancelButton
                  setOpen={setOpen}
                  isDirty={form.formState.isDirty}
                />
                <FormSubmitButton
                  submitFormId={noteModalFormId}
                  title={editing ? "Save" : "Add note"}
                  loading={isFormLoading}
                  overrideDisabled={Boolean(initialData)}
                  disabled={isFormLoading}
                />
              </div>
            </div>
          ) : null
        }
      >
        {matterError || (!isMatterLoading && !matter) ? (
          <div className="flex min-h-[min(30vh,240px)] flex-col items-center justify-center px-6 py-12 text-center">
            <Typography variant="heading" className="text-destructive">
              Could not load this matter
            </Typography>
            <Typography
              variant="subHeading"
              className="text-muted-foreground max-w-md"
            >
              Something went wrong while loading case details. Check your
              connection and try again.
            </Typography>
          </div>
        ) : (
          <fieldset
            disabled={editing ? !canEdit : false}
            className={slideoverFieldsetClassName}
          >
            <div className="col-span-2">
              <FormField
                control={form.control}
                name="message"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex">
                      <FormLabel>Note message*</FormLabel>
                      <FormMessage
                        data-testid="error-message"
                        className="grow text-right"
                      />
                    </div>
                    <FormControl>
                      <div>
                        <input
                          type="hidden"
                          name={field.name}
                          value={field.value}
                          readOnly
                          ref={field.ref}
                        />
                        <EditorContent editor={editor} />
                      </div>
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
            <div className="flex items-center justify-start">
              <SwitchFormField
                register={form.register("pinned")}
                label="Pin note"
              />
            </div>
            <div className="flex items-center justify-start">
              <SwitchFormField
                register={form.register("draft")}
                label="Save as draft"
              />
            </div>
          </fieldset>
        )}
      </AppModal>
    </Form>
  );
};
